import styled from 'styled-components'
import { type PricingLinksData } from '@/features/cms/components/CMS/types'
import { useUtmUrl } from '@/features/shared/hooks/useUtmUrl'
import { Stack } from '@shipt/design-system-layouts'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { useEffect } from 'react'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { screenSizes } from '@shipt/design-system-themes'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'

/**
 * This component corresponds to the `pricing_links` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/pricing_links
 * - **Production** - https://cms.shipt.com/content-type/pool/pricing_links
 */
export const PricingLinks = ({
  content_type_id,
  id,
  data: { primary_cta_text, scroll_to_pricing_cta_text },
}: PricingLinksData) => {
  const { trackCMSElementViewed, trackCMSElementClicked } = useCMSTrackingEvents()
  const { text: buttonText, url } = primary_cta_text
  const decoratedPrimaryUrl = useUtmUrl(url)
  const { screenLg: isLarge } = useMediaQuery()

  const scrollButtonText = scroll_to_pricing_cta_text || 'View plans'

  useEffect(() => {
    const variableTerm = () => {
      if (buttonText.includes('month') || buttonText.includes('monthly')) return 'monthly'
      return 'annual'
    }

    trackCMSElementViewed({
      content: 'pricing_links',
      message_goal: `membership_acquisition_${variableTerm()}_default`,
    })
  }, [buttonText, trackCMSElementViewed])

  return (
    <Container
      {...getCMSWrapperProps({ content_type_id, id })}
      spacing="md"
      direction={{ base: 'column', md: 'row' }}
    >
      <CTAButton
        href={decoratedPrimaryUrl}
        onClick={() => {
          trackCMSElementClicked({
            content: 'get_default',
            type: 'button',
            message_goal: buttonText,
          })
        }}
        size={isLarge ? 'lg' : 'md'}
      >
        {buttonText}
      </CTAButton>
      <CTAButton
        variant="secondary"
        href="#pricing-plans"
        onClick={(e) => {
          e.preventDefault()
          document.querySelector('#pricing-plans')?.scrollIntoView({ behavior: 'smooth' })
          trackCMSElementClicked({
            content: scrollButtonText,
            type: 'button',
          })
        }}
        size={isLarge ? 'lg' : 'md'}
      >
        {scrollButtonText}
      </CTAButton>
    </Container>
  )
}

const Container = styled(Stack)`
  width: 100%;
`

const CTAButton = styled(ButtonLink)`
  white-space: nowrap;

  @media ${screenSizes.max_mobile} {
    width: 100%;
    max-width: 100%;
  }
`
